import { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { Button, FilePicker, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { downloadBlob } from '../../../../../lib/downloadBlob';
import { useOwnerMutations } from '../../hooks';
import { OwnerSchainDataset } from '../../model';

export function OwnerSchainCsvRow(props: {
  ownerId: string;
  dataset: OwnerSchainDataset;
  count: number;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [filePickerKey, setFilePickerKey] = useState(0);
  const mutations = useOwnerMutations();
  const direct = props.dataset === OwnerSchainDataset.Direct;
  const datasetLabel = dashboardT(
    direct ? 'owners.schain.direct.title' : 'owners.schain.reseller.title',
  );
  const requiredColumns = dashboardT(
    direct ? 'owners.schain.direct.columns' : 'owners.schain.reseller.columns',
  );
  const busy =
    mutations.uploadSchain.isPending ||
    mutations.downloadSchain.isPending ||
    mutations.downloadSchainTemplate.isPending;

  async function downloadTemplate() {
    try {
      const blob = await mutations.downloadSchainTemplate.mutateAsync(props.dataset);
      downloadBlob(
        blob,
        direct ? 'owner_direct_schain_template.csv' : 'owner_reseller_schain_template.csv',
      );
    } catch (error) {
      showRequestError(error, dashboardT('owners.schain.templateError'));
    }
  }

  async function downloadCurrent() {
    if (!props.ownerId) return;
    try {
      const blob = await mutations.downloadSchain.mutateAsync({
        id: props.ownerId,
        dataset: props.dataset,
      });
      downloadBlob(
        blob,
        direct
          ? `owner_direct_schain_${props.ownerId}.csv`
          : `owner_reseller_schain_${props.ownerId}.csv`,
      );
    } catch (error) {
      showRequestError(error, dashboardT('owners.schain.downloadError'));
    }
  }

  async function upload() {
    if (!file || !props.ownerId) return;
    try {
      await mutations.uploadSchain.mutateAsync({
        id: props.ownerId,
        dataset: props.dataset,
        file,
      });
      setFile(null);
      setFilePickerKey((key) => key + 1);
      toast.success(dashboardT('owners.schain.uploaded'));
    } catch (error) {
      showRequestError(error, dashboardT('owners.schain.uploadError'));
    }
  }

  return (
    <div className="grid gap-4 px-4 py-4 lg:grid-cols-[190px_minmax(0,1fr)_auto] lg:items-end">
      <div className="self-center">
        <h3 className="text-sm font-semibold">{datasetLabel}</h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {dashboardT('owners.schain.rowCount', { count: props.count })}
        </p>
      </div>
      <div className="min-w-0">
        <FilePicker
          key={filePickerKey}
          className="min-w-0"
          label={dashboardT('owners.schain.csvFile')}
          buttonLabel={dashboardT('owners.schain.chooseFile')}
          buttonClassName="shrink-0"
          accept=".csv,text/csv"
          value={file}
          onFileChange={setFile}
          disabled={busy || !props.ownerId}
          helperText={
            props.ownerId
              ? dashboardT('owners.schain.fileHelp')
              : dashboardT('owners.schain.saveFirst')
          }
          helperTextClassName="min-w-0 flex-1 truncate text-xs"
        />
        <p className="text-muted-foreground mt-1 text-xs">{requiredColumns}</p>
      </div>
      <div className="flex flex-wrap gap-2 lg:justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void downloadTemplate()}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {dashboardT('owners.schain.template')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || !props.ownerId}
          onClick={() => void downloadCurrent()}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {dashboardT('owners.schain.download')}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy || !file || !props.ownerId}
          onClick={() => void upload()}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {dashboardT('owners.schain.upload')}
        </Button>
      </div>
    </div>
  );
}
